// src/lib/utils/autocompleteToken.ts
/*
 * The `autocomplete` token for a form field, derived from its type and name.
 *
 * None of our 29 templates set one and neither renderer emitted one, so no
 * visitor's browser has ever autofilled a KirokuForms form: 22 email fields
 * across the catalogue, none of them recognised. It is the cheapest usability
 * win available to us. The token costs nothing, works with no JavaScript, is
 * how a returning visitor fills a contact form in two taps, and is what a
 * password manager and a screen reader both read to understand the field
 * (docs/forms-research.md §5).
 *
 * Derived rather than stored, deliberately. Storing it would mean a migration,
 * a new builder control for something nobody wants to think about, and 28
 * templates to edit; deriving it means every form that already exists gets it,
 * including forms our customers built themselves.
 *
 * The names are the WHATWG autofill tokens. Anything we cannot place returns
 * null and the attribute is omitted, which is correct: a wrong token is worse
 * than none, because the browser will confidently fill the wrong value.
 */

/** Field types that map straight through. */
const BY_TYPE: Record<string, string> = {
  email: "email",
  tel: "tel",
  url: "url",
};

/*
 * Matched against the field's name and label, lowercased, first hit wins.
 * Order matters: "first name" must be tested before the bare "name", and
 * "company name" before both.
 */
const BY_NAME: ReadonlyArray<readonly [RegExp, string]> = [
  [/\b(company|organisation|organization|business)\b/, "organization"],
  [/\b(first name|given name|forename)\b/, "given-name"],
  [/\b(last name|family name|surname)\b/, "family-name"],
  [/\b(full name|your name|contact name)\b/, "name"],
  [/\bname\b/, "name"],
  [/\b(e ?mail)\b/, "email"],
  [/\b(phone|mobile|telephone|tel)\b/, "tel"],
  [/\b(job title|role|position)\b/, "organization-title"],
  [/\b(street|address line 1|address)\b/, "street-address"],
  [/\b(post ?code|postal code|zip)\b/, "postal-code"],
  [/\b(city|town)\b/, "address-level2"],
  [/\b(country)\b/, "country-name"],
  [/\b(website|url|homepage)\b/, "url"],
];

/**
 * The token for a field, or null when we cannot place it confidently.
 *
 * @param type  the field's input type
 * @param name  the field's name, which is what the form owner controls
 * @param label the visible label, used when the name is generic (`field_3`)
 */
export function autocompleteToken(
  type: string | undefined,
  name: string | undefined,
  label?: string | undefined,
): string | null {
  /*
   * Field names are split into words before matching, because none of the
   * three conventions in our own templates survives a `\b` boundary otherwise:
   * `_` and `-` are word characters, so nothing fires inside `work_email`, and
   * `fullName` has no separator at all. Both are normalised to spaces.
   */
  const haystack = `${name ?? ""} ${label ?? ""}`
    // camelCase first, while the capitals are still there: `fullName` has no
    // separator to normalise and would otherwise never match anything.
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  for (const [pattern, token] of BY_NAME) {
    if (pattern.test(haystack)) return token;
  }

  // The type is the weaker signal, so it is the fallback: a field named
  // "work_email" of type text should still be `email`, and a type="email"
  // field named "invite_a_friend" is better served by the type than by nothing.
  if (type && BY_TYPE[type]) return BY_TYPE[type];

  return null;
}
